import { Link } from 'react-router-dom'
import { useState } from 'react'
import { OptimizedPicture } from '@/shared/components/OptimizedPicture'
import { IMAGE_SPECS, PAYMENT_ICONS } from '@/shared/utils/imageAssets'

const FooterLogo = () => {
  const [imgError, setImgError] = useState(false)

  if (imgError) {
    return (
      <div className="flex items-center">
        <div className="flex gap-1">
          <div className="w-1 h-6 bg-telgo-red"></div>
          <div className="w-1 h-4 bg-telgo-red"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-16 w-[9.1rem] shrink-0 items-center">
      <OptimizedPicture
        spec={IMAGE_SPECS.logo}
        alt="TTelGo Logo"
        className="h-full w-full object-contain object-left"
        loading="lazy"
        onError={() => setImgError(true)}
      />
    </div>
  )
}

const Footer = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-white border-t border-gray-200 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <FooterLogo />
            </Link>
            <div className="mt-4 mb-2 flex justify-start">
              <span
                className="inline-block rounded-full px-4 py-2 text-xs md:text-sm lg:text-base shadow-md"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  backgroundColor: '#CC0000',
                  color: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
                }}
              >
                We Connect. Globally.
              </span>
            </div>
            <p 
              className="mb-4 max-w-md"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#5E6282'
              }}
            >
              TTelGo empowers all including travellers with scalable data plans across 200+ regions. Select by geography, usage, and time — and stay connected wherever business takes you.
            </p>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/help" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Help Center
                </Link>
              </li>
              <li>
                <Link 
                  to="/blog" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* TTelGo eSIM Column with Social Media & App Downloads */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">TTelGo eSIM</h3>
            <ul className="space-y-2 mb-6">
              <li>
                <Link 
                  to="/install-esim" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  eSIM Install Guide
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link 
                  to="#" 
                  onClick={(e) => e.preventDefault()}
                  className="hover:opacity-80 transition-opacity text-sm cursor-default"
                  style={{ color: '#5E6282' }}
                >
                  Media Center
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  About TTelGo
                </Link>
              </li>
              <li>
                <Link 
                  to="/invite-friends" 
                  className="hover:opacity-80 transition-opacity text-sm"
                  style={{ color: '#5E6282' }}
                >
                  Invite Friends
                </Link>
              </li>
            </ul>

            {/* Social Media Icons */}
            <div className="flex flex-wrap items-center gap-2 mb-4 md:flex-nowrap">
              {/* Facebook */}
              <a href="https://www.facebook.com/ttelgo" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors" aria-label="Facebook">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              {/* Instagram */}
              <a href="https://www.instagram.com/ttelgo/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity" aria-label="Instagram">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.947.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              {/* Twitter/X */}
              <a href="https://x.com/ttelgo" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors" aria-label="Twitter">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              {/* LinkedIn */}
              <a href="https://uk.linkedin.com/company/ttelgoo" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-white hover:bg-blue-800 transition-colors" aria-label="LinkedIn">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              {/* YouTube */}
              <a href="https://www.youtube.com/@ttelgo" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-700 transition-colors" aria-label="YouTube">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
              {/* TikTok */}
              <a href="https://www.tiktok.com/@ttelgo" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors" aria-label="TikTok">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
              {/* WhatsApp */}
              <a href="https://wa.me/447818984385" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors" aria-label="WhatsApp">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
            </div>

            {/* App Download Section */}
            <div className="flex flex-col">
              <p className="text-gray-600 font-medium mb-3 text-sm">Discover our app</p>
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                {/* Apple App Store Badge */}
                <Link 
                  to="/download#download-section" 
                  className="inline-block"
                >
                  <div 
                    className="bg-black rounded-md px-1.5 py-1.5 flex items-center gap-1.5 cursor-pointer h-8 hover:opacity-90 transition-opacity"
                    style={{
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      minWidth: '100px'
                    }}
                  >
                    {/* Apple Icon */}
                    <OptimizedPicture
                      spec={IMAGE_SPECS.appleStoreIcon}
                      alt="App Store"
                      className="w-5 h-5 object-contain"
                      loading="lazy"
                    />
                    <div className="flex flex-col justify-center">
                      <div className="text-[6px] text-white/90 leading-none" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif' }}>Download on the</div>
                      <div className="text-[12px] font-semibold text-white leading-none mt-0.5" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif', letterSpacing: '-0.3px' }}>App Store</div>
                    </div>
                  </div>
                </Link>
                
                {/* Google Play Store Badge */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.ttelgo.mobileapp.publicapp&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <div 
                    className="bg-black rounded-md px-1.5 py-1.5 flex items-center gap-1.5 cursor-pointer h-8 hover:opacity-90 transition-opacity"
                    style={{
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      minWidth: '100px'
                    }}
                  >
                    {/* Google Play Icon */}
                    <OptimizedPicture
                      spec={{ ...IMAGE_SPECS.storeIcon, src: '/IMAGES/Playstore.png' }}
                      alt="Google Play"
                      className="w-5 h-5 object-contain"
                      loading="lazy"
                    />
                    <div className="flex flex-col justify-center">
                      <div className="text-[6px] text-white/90 leading-none uppercase tracking-wide" style={{ fontFamily: 'Roboto, sans-serif' }}>GET IT ON</div>
                      <div className="text-[11px] font-medium text-white leading-none mt-0.5" style={{ fontFamily: 'Roboto, sans-serif', letterSpacing: '-0.5px' }}>Google Play</div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Section - Copyright on left, Payment icons on right */}
        <div className="mt-8 pt-4 border-t border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-center py-2">
            <div className="col-span-1 md:col-span-2">
              <p className="text-gray-800 text-sm font-semibold">
                TTelGo, Powered by the TikTel Ltd. (UK) {currentYear} - {currentYear + 1}
              </p>
            </div>
            <div className="hidden md:block"></div>
            {/* Payment Method Icons - aligned with TTelGo eSIM column */}
            <div className="flex items-center gap-4 flex-wrap justify-center md:justify-start">
              {PAYMENT_ICONS.map(({ src, alt, width, height }) => (
                <img
                  key={src}
                  src={src}
                  alt={alt}
                  width={width}
                  height={height}
                  loading="lazy"
                  decoding="async"
                  className="h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
